import React, { useMemo } from 'react';
import { Box, Typography, Stepper, Accordion, Button, formatPrice } from '@lucid/lego';
import type {
  VehicleOrder,
  TimelineEvent,
} from '../../../../api/services/lupp/order/importer/dto.importer';
import { formatDate } from '../../../../utils/tools';
import { useStore } from '../../../../store/store';
import { buildStepperSteps } from '../utils';
import { isOrderConfirmationAvailable } from '../utils';
import { getImporterOrderStatusLabel } from '../Orders/utils';
import { getOrderPreferenceLabel } from '../../constants';
import VehicleConfigurationPanel from './VehicleConfigurationPanel/VehicleConfigurationPanel';
import styles from './Details.module.scss';

type DetailsProps = {
  theme: 'light' | 'dark';
  order: VehicleOrder | null;
  labels: Record<string, string>;
  onBack?: () => void;
  onAccept?: (order: VehicleOrder) => void;
  onReject?: (order: VehicleOrder) => void;
  onGoToDelivery?: (order: VehicleOrder) => void;
  onOrderConfirmation?: (order: VehicleOrder) => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
};

const formatEtaBannerValue = (displayText?: string | null): string => {
  if (!displayText) {
    return 'Unknown';
  }

  const normalized = displayText.replace(/\bweeks\b/i, 'Weeks').replace(/\bweek\b/i, 'Week');

  return normalized === 'Unknown' ? normalized : `~ ${normalized}`;
};

const formatDisplayPrice = (amount: number, locale: string): string =>
  formatPrice(amount, { locale });

/**
 * Order Desk — Vehicle Order Details page.
 * Status stepper at the top, then accordions for Details, Vehicle Details, and Timeline.
 * @component
 * @param props.theme - Theme for the component
 * @param props.order - Vehicle order data to display
 * @param props.labels - i18n labels
 * @param props.onAccept - Handler for accepting the order
 * @param props.onGoToDelivery - Handler for navigating to delivery
 * @param props.onOrderConfirmation - Handler for downloading the child order confirmation
 * @returns {JSX.Element | null}
 */
const Details: React.FC<DetailsProps> = ({
  theme,
  order,
  labels,
  onAccept,
  onReject,
  onGoToDelivery,
  onOrderConfirmation,
  onBack,
  isAccepting = false,
  isRejecting = false,
}) => {
  const locale = useStore((state) => state.locale);
  const stepperSteps = useMemo(
    () => buildStepperSteps(order?.vehicleJourneyStatus),
    [order?.vehicleJourneyStatus]
  );

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const renderOverviewField = (label: string, value: string | null | undefined) => (
    <Box className={styles.overviewField}>
      <Typography variant="label" size="small" theme={invertedTheme} color="gray-4">
        {label}
      </Typography>
      <Typography variant="body" size="medium" theme={invertedTheme} color="white">
        {value ?? '-'}
      </Typography>
    </Box>
  );

  /*
  |--------------------------------------------------------------------------
  | Timeline accordion content
  |--------------------------------------------------------------------------
  */
  const renderTimelineContent = (timeline: TimelineEvent[]) => (
    <Box className={styles.accordionBody}>
      <Box className={styles.timeline}>
        {timeline.map((event, index) => {
          const isLast = index === timeline.length - 1;
          return (
            <Box key={event.id} className={styles.timelineItem}>
              <Box className={styles.timelineLeft}>
                <Box className={styles.timelineDot} />
                {!isLast && <Box className={styles.timelineLine} />}
              </Box>
              <Box className={styles.timelineContent}>
                <Typography variant="label" size="small" theme={invertedTheme} color="gray-4">
                  {formatDate(event.timestamp)}
                </Typography>
                <Typography variant="body" size="small" theme={invertedTheme} color="white">
                  {event.label}
                </Typography>
                {event.description && (
                  <Typography variant="body" size="extraSmall" theme={invertedTheme} color="gray-4">
                    {event.description}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */
  if (!order) return null;

  const timeline = order.timeline ?? [];

  const isVinAssigned = Boolean(order.vin ?? order.vehicle?.vin);
  const isInReview = (order.statusName ?? '').trim().toUpperCase() === 'IN REVIEW';
  const hideVehicleDetails = !isVinAssigned && isInReview;

  return (
    <Box testId="order-details-page">
      {/* Header: order number + subtitle + back + action buttons */}
      <Box className={styles.pageHeader}>
        <Box className={styles.pageHeaderLeft}>
          <Typography variant="headline" size="small" theme={theme} color="primary-1">
            {`${labels?.['details.orderPrefix'] ?? 'Order'} ${order.orderNumber ?? ''}`}
          </Typography>
          <Box pt={8} pb={12}>
            <Typography variant="body" size="medium" theme={theme} color="gray-3">
              {labels?.['details.subtitle'] ?? 'Manage and track all bulk orders.'}
            </Typography>
          </Box>
          {onBack && (
            <Button
              type="tertiary"
              color="neutral"
              theme={theme}
              dataTestId="details-back-button"
              gaComponent="order-details"
              label={(
                labels?.['page.backToLineItems'] ??
                `Back to ${labels?.['page.lineItems'] ?? 'Line Items'}`
              ).replace('{{orderNumber}}', order.importerOrderNumber ?? '')}
              iconLeft={{ category: 'arrows', name: 'arrow-left' }}
              handleClick={onBack}
            />
          )}
        </Box>
        <Box className={styles.headerActions}>
          {order.statusName === 'Waiting for Confirmation' && (
            <>
              {onReject && (
                <Button
                  type="secondary"
                  color="neutral"
                  size="small"
                  theme={theme}
                  label={labels?.['details.reject'] ?? 'Reject'}
                  dataTestId="details-reject-button"
                  gaComponent="order-details"
                  disabled={isRejecting || isAccepting}
                  handleClick={() => onReject(order)}
                />
              )}
              {onAccept && (
                <Button
                  type="primary"
                  color="neutral"
                  size="small"
                  theme={theme}
                  iconLeft={{ category: 'global', name: 'check-circle' }}
                  label={labels?.['details.accept'] ?? 'Accept Order'}
                  dataTestId="details-accept-button"
                  gaComponent="order-details"
                  disabled={isAccepting || isRejecting}
                  handleClick={() => onAccept(order)}
                />
              )}
            </>
          )}
          {order.statusName === 'Confirmed' && onGoToDelivery && (
            <Button
              type="primary"
              color="neutral"
              size="small"
              theme={theme}
              label={labels?.['details.goToDelivery'] ?? 'Go to Delivery'}
              dataTestId="details-go-to-delivery-button"
              gaComponent="order-details"
              handleClick={() => onGoToDelivery(order)}
            />
          )}
          {isOrderConfirmationAvailable(order) && onOrderConfirmation && (
            <Button
              type="secondary"
              color="neutral"
              size="small"
              theme={theme}
              label={labels?.['table.orderConfirmation'] ?? 'Order Confirmation'}
              dataTestId="details-order-confirmation-button"
              gaComponent="order-details"
              disabled={!order.orderNumber}
              handleClick={() => onOrderConfirmation(order)}
            />
          )}
        </Box>
      </Box>

      {/* Status stepper + Overview card */}
      {!hideVehicleDetails && (
        <Box
          mb={24}
          mt={16}
          background="white"
          pt={16}
          pb={16}
          pl={16}
          pr={16}
          className={styles.stepperContainer}
        >
          <Box className={styles.cardHeader}>
            <Typography variant="label" size="large" theme={invertedTheme} color="white">
              {labels?.['details.vehicleDetails'] ?? 'Vehicle Details'}
            </Typography>
          </Box>
          <Stepper steps={stepperSteps} theme={invertedTheme} dataTestId="order-status-stepper" />

          {/* Overview fields grid */}
          <Box className={styles.overviewGrid}>
            {renderOverviewField(
              labels?.['details.orderDate'] ?? 'Order Date',
              formatDate(order.orderDate)
            )}
            {renderOverviewField(
              labels?.['details.orderStatus'] ?? 'Order Status',
              order.statusName ? getImporterOrderStatusLabel(order.statusName, labels) : undefined
            )}
            {renderOverviewField(
              labels?.['details.orderType'] ?? 'Order Type',
              getOrderPreferenceLabel(order.orderPreference, labels)
            )}
            {renderOverviewField(
              labels?.['details.totalPrice'] ?? 'Total Price',
              formatDisplayPrice(order.totalAmount ?? 0, locale)
            )}
            {renderOverviewField(
              labels?.['details.parentOrderNumber'] ?? 'Parent Order #',
              order.importerOrderNumber
            )}
            {renderOverviewField(
              labels?.['details.vin'] ?? 'VIN #',
              order.vin ?? order.vehicle?.vin
            )}
            {renderOverviewField(
              labels?.['details.scheduledDeliveryDate'] ?? 'Scheduled Delivery Date',
              formatDate(order.scheduledDeliveryDate)
            )}
            {renderOverviewField(
              labels?.['details.handoffDate'] ?? 'Handoff Date',
              formatDate(order.handoffDate)
            )}
            {renderOverviewField(labels?.['details.location'] ?? 'Location', order.location)}
            <Box className={styles.overviewField}>
              <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                {labels?.['details.estimatedTimeOfArrival'] ?? 'Estimated Time of Arrival'}
              </Typography>
              <Typography variant="body" size="medium" theme={invertedTheme} color="primary-1">
                {formatEtaBannerValue(order.eta?.displayText)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Timeline accordion */}
      {timeline.length > 0 && (
        <Box className={styles.accordionSection}>
          <Accordion
            title={labels?.['details.timeline'] ?? 'Timeline'}
            theme={theme}
            defaultOpen
            dataTestId="order-timeline-accordion"
          >
            {renderTimelineContent(timeline)}
          </Accordion>
        </Box>
      )}

      {/* Vehicle Configuration accordion */}
      {order.checkoutDetail?.checkoutInfo?.vehicleInfo?.length ? (
        <Box className={styles.accordionSection}>
          <Accordion
            title={labels?.['details.vehicleConfiguration'] ?? 'Vehicle Configuration'}
            theme={theme}
            defaultOpen
            dataTestId="vehicle-configuration-accordion"
          >
            <Box className={styles.vehicleConfigLayout}>
              {order.checkoutDetail?.vehicleDetails?.imageUrl && (
                <Box className={styles.vehicleConfigImage}>
                  <img
                    src={order.checkoutDetail.vehicleDetails.imageUrl}
                    alt={labels?.['details.vehicleImage'] ?? 'Vehicle'}
                    className={styles.vehicleImg}
                  />
                </Box>
              )}
              <Box className={styles.vehicleConfigLeft}>
                <VehicleConfigurationPanel
                  dataTestId="vehicle-configuration-panel"
                  checkoutDetail={order.checkoutDetail}
                  theme={theme}
                  labels={labels}
                />
              </Box>
            </Box>
          </Accordion>
        </Box>
      ) : null}
    </Box>
  );
};

export default Details;
